import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  ArrowLeft,
  X,
  Save,
  Trash2,
  ChevronRight,
  MapPin,
  Image as ImageIcon,
} from "lucide-react";

const STATUS_OPTIONS = [
  { v: "new", label: "New" },
  { v: "reviewing", label: "Reviewing" },
  { v: "listed", label: "Listed" },
  { v: "under_contract", label: "Under Contract" },
  { v: "sold", label: "Sold" },
  { v: "dead", label: "Dead" },
];

const STATUS_COLOR = {
  new: "bg-[#C5A059]/20 text-[#8A6A2C]",
  reviewing: "bg-[#0B1B3D]/10 text-[#0B1B3D]",
  listed: "bg-[#2C5545]/15 text-[#2C5545]",
  under_contract: "bg-blue-100 text-blue-800",
  sold: "bg-green-100 text-green-800",
  dead: "bg-red-100 text-red-800",
};

export default function AdminDeals() {
  const [deals, setDeals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // deal being viewed/edited
  const [filter, setFilter] = useState("");
  const [photoOpen, setPhotoOpen] = useState(null); // photo dataUrl to lightbox

  const load = async () => {
    try {
      setLoading(true);
      const [l, s] = await Promise.all([api.get("/deals"), api.get("/deals/stats")]);
      setDeals(l.data);
      setStats(s.data);
    } catch (e) {
      toast.error(e?.message || "Failed to load deals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);

  const filtered = filter ? deals.filter((d) => d.status === filter) : deals;

  return (
    <main data-testid="admin-deals-page" className="min-h-screen bg-[#FAF9F6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <Link
          to="/admin/leads"
          className="inline-flex items-center gap-1.5 text-xs text-[#64748B] hover:text-[#0B1B3D] mb-4"
        >
          <ArrowLeft className="h-3 w-3" /> Back to Leads
        </Link>

        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-[#2C5545]">
              Clear Path Home Relief Services
            </div>
            <h1 className="font-display text-3xl lg:text-4xl text-[#0B1B3D]">
              Wholesale Deals
            </h1>
            <p className="text-xs text-[#64748B] mt-1">
              Broker only · Set the price and status · Live refresh every 10s
            </p>
          </div>
          <button
            onClick={load}
            className="px-4 py-2 text-sm rounded-md border border-[#0B1B3D] text-[#0B1B3D] hover:bg-white"
          >
            Refresh
          </button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
            <StatCard k={stats.total} v="Total" onClick={() => setFilter("")} active={filter === ""} />
            <StatCard k={stats.new} v="New" onClick={() => setFilter("new")} active={filter === "new"} />
            <StatCard k={stats.reviewing} v="Reviewing" onClick={() => setFilter("reviewing")} active={filter === "reviewing"} />
            <StatCard k={stats.listed} v="Listed" onClick={() => setFilter("listed")} active={filter === "listed"} />
            <StatCard k={stats.under_contract} v="Contract" onClick={() => setFilter("under_contract")} active={filter === "under_contract"} />
            <StatCard k={stats.sold} v="Sold" onClick={() => setFilter("sold")} active={filter === "sold"} />
            <StatCard k={stats.dead} v="Dead" onClick={() => setFilter("dead")} active={filter === "dead"} />
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-[#64748B]">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading deals...
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-[#E5E3DB] bg-white p-10 text-center text-[#64748B]">
            No deals {filter && `in "${filter}"`} yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map((d) => (
              <DealCard
                key={d.id}
                deal={d}
                onOpen={() => setSelected(d)}
                onPhoto={(p) => setPhotoOpen(p)}
              />
            ))}
          </div>
        )}
      </div>

      {selected && (
        <DealDrawer
          deal={selected}
          onClose={() => setSelected(null)}
          onSaved={(fresh) => {
            setSelected(fresh);
            setDeals((prev) => prev.map((x) => (x.id === fresh.id ? fresh : x)));
          }}
          onDeleted={() => {
            setDeals((prev) => prev.filter((x) => x.id !== selected.id));
            setSelected(null);
          }}
          onPhoto={(p) => setPhotoOpen(p)}
        />
      )}

      {photoOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-6"
          onClick={() => setPhotoOpen(null)}
          data-testid="deal-photo-lightbox"
        >
          <button
            onClick={() => setPhotoOpen(null)}
            className="absolute top-6 right-6 text-white/80 hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
          <img src={photoOpen} alt="deal photo" className="max-h-[90vh] max-w-[95vw] object-contain rounded-lg" />
        </div>
      )}
    </main>
  );
}

function StatCard({ k, v, onClick, active }) {
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-lg p-4 border transition-colors ${
        active
          ? "bg-[#0B1B3D] border-[#0B1B3D] text-white"
          : "bg-white border-[#E5E3DB] hover:border-[#0B1B3D]/40 text-[#0B1B3D]"
      }`}
    >
      <div className={`font-display text-2xl leading-none ${active ? "text-[#C5A059]" : "text-[#0B1B3D]"}`}>{k}</div>
      <div className={`text-[10px] uppercase tracking-[0.18em] mt-2 ${active ? "text-white/70" : "text-[#64748B]"}`}>
        {v}
      </div>
    </button>
  );
}

function DealCard({ deal, onOpen, onPhoto }) {
  const cover = deal.photos?.[0];
  const location = [deal.city, deal.state, deal.zip_code].filter(Boolean).join(", ");
  return (
    <div
      data-testid={`deal-card-${deal.id}`}
      className="rounded-lg border border-[#E5E3DB] bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow"
    >
      <div
        className="aspect-video bg-[#F3F2EE] relative cursor-pointer"
        onClick={() => cover && onPhoto(cover)}
      >
        {cover ? (
          <img src={cover} alt="cover" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#64748B]">
            <ImageIcon className="h-8 w-8" />
          </div>
        )}
        {deal.photos?.length > 1 && (
          <div className="absolute bottom-2 right-2 text-[11px] bg-black/60 text-white rounded px-2 py-0.5">
            +{deal.photos.length - 1} photos
          </div>
        )}
        <span
          className={`absolute top-2 left-2 text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-1 rounded ${STATUS_COLOR[deal.status] || ""}`}
        >
          {deal.status?.replace("_", " ") || "new"}
        </span>
      </div>

      <div className="p-4">
        <div className="font-display text-lg text-[#0B1B3D] leading-tight">
          {deal.property_address}
        </div>
        {location && (
          <div className="text-xs text-[#64748B] flex items-center gap-1 mt-1">
            <MapPin className="h-3 w-3" /> {location}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-1.5 text-[11px] text-[#64748B]">
          {deal.property_type && <Chip>{deal.property_type}</Chip>}
          {deal.beds && <Chip>{deal.beds} bd</Chip>}
          {deal.baths && <Chip>{deal.baths} ba</Chip>}
          {deal.sqft && <Chip>{deal.sqft} sqft</Chip>}
          {deal.condition && <Chip>{deal.condition}</Chip>}
        </div>
        <div className="mt-3 flex items-center justify-between text-sm">
          <div>
            {deal.asking_price && (
              <div className="text-xs text-[#64748B]">
                Wholesaler asking: <span className="text-[#0B1B3D] font-medium">{deal.asking_price}</span>
              </div>
            )}
            {deal.broker_price ? (
              <div className="text-[#0B1B3D] font-semibold">Broker: {deal.broker_price}</div>
            ) : (
              <div className="text-[#991B1B] text-xs font-medium">Broker price not set</div>
            )}
            {deal.arv && <div className="text-xs text-[#64748B]">ARV: {deal.arv}</div>}
          </div>
          <button
            onClick={onOpen}
            data-testid={`deal-open-${deal.id}`}
            className="inline-flex items-center gap-1 text-sm text-[#0B1B3D] font-medium hover:text-[#C5A059]"
          >
            Open <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="rounded-full bg-[#F3F2EE] text-[#0B1B3D] px-2 py-0.5">
      {children}
    </span>
  );
}

function DealDrawer({ deal, onClose, onSaved, onDeleted, onPhoto }) {
  const [edit, setEdit] = useState({
    broker_price: deal.broker_price || "",
    broker_notes: deal.broker_notes || "",
    status: deal.status || "new",
    arv: deal.arv || "",
    repair_estimate: deal.repair_estimate || "",
    notes: deal.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.keys(edit).forEach((k) => {
        if (edit[k] !== "" && edit[k] !== (deal[k] || "")) payload[k] = edit[k];
      });
      if (Object.keys(payload).length === 0) {
        toast.info("Nothing to save.");
        setSaving(false);
        return;
      }
      const res = await api.patch(`/deals/${deal.id}`, payload);
      onSaved(res.data);
      toast.success("Deal updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    setSaving(true);
    try {
      await api.delete(`/deals/${deal.id}`);
      toast.success("Deal deleted.");
      onDeleted();
    } catch (e) {
      toast.error("Delete failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-stretch justify-end" onClick={onClose}>
      <div
        className="w-full max-w-2xl bg-white overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="deal-drawer"
      >
        <div className="sticky top-0 z-10 bg-white border-b border-[#E5E3DB] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#2C5545]">
              Deal detail
            </div>
            <div className="font-display text-xl text-[#0B1B3D]">
              {deal.property_address}
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-[#64748B] hover:text-[#0B1B3D]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Photos */}
          {deal.photos?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#0B1B3D] font-semibold mb-2">
                Photos ({deal.photos.length})
              </div>
              <div className="grid grid-cols-3 gap-2">
                {deal.photos.map((p, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => onPhoto(p)}
                    className="aspect-square rounded overflow-hidden border border-[#E5E3DB]"
                  >
                    <img src={p} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Broker-only fields */}
          <div className="rounded-lg border border-[#C5A059]/40 bg-[#C5A059]/10 p-4 space-y-4">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#0B1B3D] font-semibold">
              Broker only
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldLine label="Broker Price">
                <input
                  data-testid="drawer-broker-price"
                  className="w-full h-10 px-3 rounded border border-[#E5E3DB] bg-white"
                  value={edit.broker_price}
                  onChange={(e) => setEdit((s) => ({ ...s, broker_price: e.target.value }))}
                  placeholder="$185,000"
                />
              </FieldLine>
              <FieldLine label="Status">
                <select
                  data-testid="drawer-status"
                  className="w-full h-10 px-3 rounded border border-[#E5E3DB] bg-white"
                  value={edit.status}
                  onChange={(e) => setEdit((s) => ({ ...s, status: e.target.value }))}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.v} value={o.v}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </FieldLine>
            </div>
            <FieldLine label="Broker Notes (private)">
              <textarea
                data-testid="drawer-broker-notes"
                className="w-full min-h-[80px] px-3 py-2 rounded border border-[#E5E3DB] bg-white text-sm"
                value={edit.broker_notes}
                onChange={(e) => setEdit((s) => ({ ...s, broker_notes: e.target.value }))}
                placeholder="Buyer target, comps, negotiation notes..."
              />
            </FieldLine>
          </div>

          {/* Editable info */}
          <div className="space-y-4">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#0B1B3D] font-semibold">
              Deal info (editable)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldLine label="ARV">
                <input
                  className="w-full h-10 px-3 rounded border border-[#E5E3DB] bg-white"
                  value={edit.arv}
                  onChange={(e) => setEdit((s) => ({ ...s, arv: e.target.value }))}
                />
              </FieldLine>
              <FieldLine label="Repair Estimate">
                <input
                  className="w-full h-10 px-3 rounded border border-[#E5E3DB] bg-white"
                  value={edit.repair_estimate}
                  onChange={(e) => setEdit((s) => ({ ...s, repair_estimate: e.target.value }))}
                />
              </FieldLine>
            </div>
            <FieldLine label="Notes (visible)">
              <textarea
                className="w-full min-h-[80px] px-3 py-2 rounded border border-[#E5E3DB] bg-white text-sm"
                value={edit.notes}
                onChange={(e) => setEdit((s) => ({ ...s, notes: e.target.value }))}
              />
            </FieldLine>
          </div>

          {/* Read-only property snapshot */}
          <div className="rounded-lg border border-[#E5E3DB] p-4 bg-[#FAF9F6] text-sm">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#64748B] font-semibold mb-2">
              Property snapshot
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[#0F172A]">
              <dt className="text-[#64748B]">Location</dt>
              <dd>{[deal.city, deal.state, deal.zip_code].filter(Boolean).join(", ") || "—"}</dd>
              <dt className="text-[#64748B]">Type</dt>
              <dd>{deal.property_type || "—"}</dd>
              <dt className="text-[#64748B]">Beds / Baths</dt>
              <dd>{[deal.beds, deal.baths].filter(Boolean).join(" / ") || "—"}</dd>
              <dt className="text-[#64748B]">Sqft / Year</dt>
              <dd>{[deal.sqft, deal.year_built].filter(Boolean).join(" / ") || "—"}</dd>
              <dt className="text-[#64748B]">Condition / Occupancy</dt>
              <dd>{[deal.condition, deal.occupancy].filter(Boolean).join(" / ") || "—"}</dd>
              <dt className="text-[#64748B]">Wholesaler Asking</dt>
              <dd>{deal.asking_price || "—"}</dd>
              <dt className="text-[#64748B]">Rent</dt>
              <dd>{deal.monthly_rent || "—"}</dd>
            </dl>
          </div>

          {/* Wholesaler */}
          <div className="rounded-lg border border-[#E5E3DB] p-4 bg-white text-sm">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#64748B] font-semibold mb-2">
              Wholesaler
            </div>
            <div className="font-medium text-[#0B1B3D]">{deal.wholesaler_name}</div>
            {deal.wholesaler_company && (
              <div className="text-xs text-[#64748B]">{deal.wholesaler_company}</div>
            )}
            <div className="mt-1 text-[#0F172A]">
              <a href={`tel:${deal.wholesaler_phone}`} className="hover:text-[#C5A059]">
                {deal.wholesaler_phone}
              </a>
              {deal.wholesaler_email && (
                <>
                  {" · "}
                  <a href={`mailto:${deal.wholesaler_email}`} className="hover:text-[#C5A059]">
                    {deal.wholesaler_email}
                  </a>
                </>
              )}
            </div>
            <div className="text-[11px] text-[#64748B] mt-2">
              Submitted {new Date(deal.created_at).toLocaleString()}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-2">
            <button
              onClick={save}
              disabled={saving}
              data-testid="drawer-save"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#0B1B3D] text-white hover:bg-[#1A2F5C] disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save changes
            </button>
            {!confirmDel ? (
              <button
                onClick={() => setConfirmDel(true)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-red-200 text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            ) : (
              <div className="inline-flex items-center gap-2">
                <span className="text-sm text-red-700">Sure?</span>
                <button
                  onClick={del}
                  disabled={saving}
                  className="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700"
                >
                  Yes, delete
                </button>
                <button
                  onClick={() => setConfirmDel(false)}
                  className="px-4 py-2 text-sm rounded border border-[#E5E3DB]"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldLine({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.14em] text-[#0F172A]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
